import { Component, OnDestroy, OnInit, input } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { CsIconModule } from '@components/icon';
import { BroadcastService } from '@services/broadcast-service';
import { CartService } from '@services/cart-service/cart.service';
import { ConnectionService } from '@services/connection-service/index';
import { EventLoggerService } from '@services/event-logger-service';
import { RecommendedProductsSurface } from '@services/experiment/experiment.service.types';
import { CartUpdateOptions } from '@shared/types/cart.types';
import { fromActions } from '@store/actions';
import { fromSelectors } from '@store/selectors';
import { AppConfig } from '../../app.config';
import {
  RecommendedProduct,
  RecommendedCartLineItem,
} from './recommended-products.types';

// Client-side localization for the two tags the API only returns in English (ml pending).
const TAG_TRANSLATIONS: Record<string, Record<string, string>> = {
  'Complete your kit': {
    hi: 'अपना किट पूरा करें',
    mr: 'तुमचे किट पूर्ण करा',
    te: 'మీ కిట్ పూర్తి చేయండి',
    kn: 'ನಿಮ್ಮ ಕಿಟ್ ಪೂರ್ಣಗೊಳಿಸಿ',
    ta: 'உங்கள் கிட்டை முழுமையாக்குங்கள்',
    bn: 'আপনার কিট সম্পূর্ণ করুন',
  },
  'Doctor Suggested': {
    hi: 'डॉक्टर की सलाह',
    mr: 'डॉक्टरांचा सल्ला',
    te: 'డాక్టర్ సిఫార్సు',
    kn: 'ವೈದ್ಯರ ಶಿಫಾರಸು',
    ta: 'மருத்துவர் பரிந்துரை',
    bn: 'ডাক্তারের পরামর্শ',
  },
};

@Component({
  selector: 'recommended-products',
  templateUrl: './recommended-products.html',
  standalone: true,
  imports: [CsIconModule],
  host: { class: 'tw-block' },
})
export class RecommendedProductsComponent implements OnInit, OnDestroy {
  readonly bannerName = input<string>('recommendedProducts');
  readonly products = input<RecommendedProduct[]>([]);
  readonly displayType = input<string>('');

  private readonly collectionName: string = 'recommendedProducts';
  private readonly taggedCardsDisplayType: string = 'TAGGED_CARDS';
  readonly cartAction = this.appConfig.Shared.Cart.Action;

  productsInCart: Record<string, number> = {};
  cartItems: RecommendedCartLineItem[] = [];
  private isCartOperationPending: boolean = false;
  private subscriptions: Subscription[] = [];

  constructor(
    private conn: ConnectionService,
    private cartService: CartService,
    private broadcast: BroadcastService,
    private store: Store,
    private appConfig: AppConfig,
    private eventLogger: EventLoggerService,
  ) {}

  async ngOnInit(): Promise<void> {
    if (this.products().length) await this.getCart();

    this.subscriptions.push(
      this.store.select(fromSelectors.selectCartProducts)
        .subscribe((products: Record<string, number>): void => { this.productsInCart = { ...products }; }),
      this.broadcast.on('REFRESH_SHOP_CART').subscribe((): void => { this.getCart(); }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription): void => subscription.unsubscribe());
  }

  get isSectionVisible(): boolean {
    return !!this.products().length && this.displayType() === this.taggedCardsDisplayType;
  }

  private get from(): RecommendedProductsSurface {
    return this.bannerName().startsWith('home') ? RecommendedProductsSurface.Home : RecommendedProductsSurface.Shop;
  }

  private logClick(name: string, productId?: string): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name, pageName: this.from, productId }));
  }

  async getCart(): Promise<void> {
    const cart = await this.conn.fetchNewCart(this.conn.getActingUser()?.id);
    const lineItems: Array<{ productId?: string; quantity?: number }> = cart?.data?.lineItems ?? [];
    this.cartItems = lineItems.map((item): RecommendedCartLineItem => ({ productId: item.productId ?? '', quantity: item.quantity ?? 0 }));
    const quantities: Record<string, number> = {};
    this.cartItems.forEach((item: RecommendedCartLineItem): void => { quantities[item.productId] = item.quantity; });
    this.productsInCart = quantities;
  }

  translateTag(tag?: string): string {
    if (!tag) return '';
    return TAG_TRANSLATIONS[tag]?.[this.conn.getUserLanguage] ?? tag;
  }

  productImage(product: RecommendedProduct): string {
    return product?.rebrandedImageWithBackground?.[0]
      || product?.productUnboxedImage?.[0]
      || product?.images?.[0]
      || product?.productImage;
  }

  hasDiscount(product: RecommendedProduct): boolean {
    return !!product?.mrp && product.mrp > product.price;
  }

  quantityInCart(product: RecommendedProduct): number {
    return this.productsInCart[product?.objectId] || 0;
  }

  isInCart(product: RecommendedProduct): boolean {
    return this.quantityInCart(product) > 0;
  }

  async updateCart(product: RecommendedProduct, action: string, event: Event, image?: HTMLImageElement): Promise<void> {
    event.stopPropagation();
    if (this.isCartOperationPending) return;
    this.isCartOperationPending = true;
    const isAdd = action === this.cartAction.ADD;
    this.logClick(isAdd ? 'add-to-cart-from-recommended' : 'remove-from-cart-from-recommended', product?.objectId);
    try {
      const options: CartUpdateOptions = {
        img: image,
        productsInCart: this.productsInCart || {},
        cartItems: this.cartItems || [],
        section: this.bannerName(),
      };
      const result = await this.cartService.addOrRemoveFromCart(product, action, options);
      if (result.success) {
        if (result.updatedQuantity > 0) this.productsInCart[product.objectId] = result.updatedQuantity;
        else delete this.productsInCart[product.objectId];
        this.store.dispatch(fromActions.CartUpdateProductsBegin({ products: this.productsInCart }));
        this.broadcast.broadcast('REFRESH_SHOP_CART');
      }
    } finally {
      this.isCartOperationPending = false;
    }
  }

  viewProduct(product: RecommendedProduct): void {
    this.logClick('view-product-from-recommended', product?.objectId);
    this.conn.navigateToURL(`/product/${product.objectId}?section=${this.bannerName()}`);
  }

  viewAll(): void {
    this.logClick('view-all-from-recommended');
    this.conn.navigateToURL(`/user/order/reorder?from=${this.collectionName}&type=collection&source=${this.from}`);
  }
}
